import { useMemo, useState } from 'react'

import type {
  FlightPlayerScoresFragment,
  HoleScoreFragment,
} from '../../../__generated__/hooks'
import type { CurrentPlayerCtx } from '../../../PlayerShell'
import { groupByTeam } from '../../../util/best-ball'
import { isNetStrokes } from '../../../util/event-mode'
import { initialsOf } from '../../../util/initials'
import Legend from '../components/Legend'
import Table from '../components/Table'

type View = 'bestball' | 'individual'

/**
 * Best-ball scorecard view. Adds a `Best ball / Individuelt` toggle:
 *  - team view: columns become synthesised team rows whose `scores`
 *    are the team's best-of per hole; the contributing player's
 *    initials get stamped under each cell.
 *  - individual view: per-player columns, same as plain Golf.
 *
 * Both modes go through the same `Table` component — only `flight` and
 * `cellSubLabel` differ.
 */
const BestBallView = ({
  player,
  flight,
  holes,
}: {
  player: CurrentPlayerCtx
  flight: FlightPlayerScoresFragment[]
  holes: HoleScoreFragment[]
}) => {
  const [view, setView] = useState<View>('bestball')

  const teamSynthEntries = useMemo<FlightPlayerScoresFragment[]>(() => {
    const seen = new Map<string, FlightPlayerScoresFragment>()
    for (const e of flight) {
      const tId = e.team?.id
      if (!tId || seen.has(tId)) continue
      seen.set(tId, {
        ...e,
        id: tId,
        name: e.team?.name ?? 'Uten navn',
        hcp: undefined,
        scores: e.teamScores ?? [],
        accumulated: e.teamAccumulated ?? e.accumulated,
      })
    }
    return Array.from(seen.values())
  }, [flight])

  // Per-hole-per-team contributor initials: { teamId: { holeNumber: "LT" } }.
  const contributorInitialsByTeam = useMemo<
    Record<string, Record<number, string>>
  >(() => {
    const out: Record<string, Record<number, string>> = {}
    for (const h of holes) {
      const groups = groupByTeam(flight, h.hole.number)
      for (const g of groups) {
        const map = out[g.teamId] ?? {}
        if (g.bestForHole.contributorId) {
          const contributor = g.members.find(
            (m) => m.id === g.bestForHole.contributorId
          )
          if (contributor) {
            map[h.hole.number] = initialsOf(contributor.name)
          }
        }
        out[g.teamId] = map
      }
    }
    return out
  }, [flight, holes])

  // Individual fallback sort — player first in their own column.
  const sortedFlight = useMemo(
    () =>
      [...flight].sort((a, b) => {
        if (a.id === player.id) return -1
        if (b.id === player.id) return 1
        return 0
      }),
    [flight, player.id]
  )

  // Stableford ranking → show per-hole stableford points in the individual
  // view. Suppressed in the best-ball team view (the cell already carries the
  // contributor initials in that slot).
  const showStableford =
    view === 'individual' && !isNetStrokes(player.polfEvent.ranking)

  const visibleFlight = view === 'bestball' ? teamSynthEntries : sortedFlight
  const cellSubLabel =
    view === 'bestball'
      ? (entryId: string, holeNumber: number) =>
          contributorInitialsByTeam[entryId]?.[holeNumber] ?? null
      : undefined

  return (
    <section className="pb-6">
      <div className="px-4 pt-4 pb-2 flex items-end justify-between">
        <h1 className="font-display text-xl font-bold text-green-900">
          Scorekort
        </h1>
        <p className="text-xs text-ink-500 num">
          {holes.length} hull · {visibleFlight.length}{' '}
          {view === 'bestball' ? 'lag' : 'spillere'}
        </p>
      </div>
      <div className="px-2 pb-2">
        <div
          role="tablist"
          className="inline-flex rounded-full bg-ink-100 p-1 text-xs font-bold uppercase tracking-wider"
        >
          {(['bestball', 'individual'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={view === m}
              onClick={() => setView(m)}
              className={`px-3 py-1.5 rounded-full transition-colors ${
                view === m ? 'bg-paper text-ink-900 shadow' : 'text-ink-500'
              }`}
            >
              {m === 'bestball' ? 'Best ball' : 'Individuelt'}
            </button>
          ))}
        </div>
      </div>
      <Table
        player={player}
        flight={visibleFlight}
        holes={holes}
        cellSubLabel={cellSubLabel}
        showStableford={showStableford}
      />
      <Legend />
    </section>
  )
}

export default BestBallView
